'use strict';

/**
 * Pict-Provider-MoodboardAPI
 * --------------------------
 * Browser-side CRUD client for the Moodboard entity. Extends the generic EntityAPI provider with two
 * helpers for the board blob: a Moodboard row carries the whole canvas in its BoardJSON column (the
 * { Nodes, Connections, ViewState } shape from pict-section-moodboard / pict-section-flow).
 *
 * loadBoard takes a Moodboard row and returns the parsed board object (handling either a JSON string
 * or an already-parsed object, since how meadow hands back a JSON column can vary). saveBoard writes a
 * board object back to a row. Tenancy (IDCustomer) is enforced server-side, so the client never sends
 * it.
 */

const libPictProviderEntityAPI = require('./Pict-Provider-EntityAPI.js');
const libFrameworkClient = require('./FrameworkClient.js');

const _DefaultProviderConfiguration =
{
	ProviderIdentifier: 'MoodboardAPI',
	Dependencies: [],
	RoutePrefix: '/1.0/',
	EntityName: 'Moodboard',
	PluralRoute: 'Moodboards',
	IDField: 'IDMoodboard'
};

const _EMPTY_BOARD = { Nodes: [], Connections: [], ViewState: { PanX: 0, PanY: 0, Zoom: 1 } };

class PictProviderMoodboardAPI extends libPictProviderEntityAPI
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, Object.assign({}, _DefaultProviderConfiguration, pOptions || {}), pServiceHash);
	}

	/**
	 * Parse a Moodboard row's BoardJSON into a board object. Tolerates a string, an object, or an
	 * empty/missing value (a brand-new board).
	 * @param {object} pRecord - a Moodboard row
	 * @returns {object} the board { Nodes, Connections, ViewState }
	 */
	loadBoard(pRecord)
	{
		if (!pRecord) { return JSON.parse(JSON.stringify(_EMPTY_BOARD)); }
		let tmpRaw = pRecord.BoardJSON;
		if (!tmpRaw) { return JSON.parse(JSON.stringify(_EMPTY_BOARD)); }
		if (typeof tmpRaw === 'object') { return tmpRaw; }
		try
		{
			let tmpParsed = JSON.parse(tmpRaw);
			return (tmpParsed && typeof tmpParsed === 'object') ? tmpParsed : JSON.parse(JSON.stringify(_EMPTY_BOARD));
		}
		catch (pError)
		{
			return JSON.parse(JSON.stringify(_EMPTY_BOARD));
		}
	}

	/**
	 * Read one Moodboard row fresh from the server (authoritative BoardJSON), bypassing the list cache.
	 * @param {number} pID - IDMoodboard
	 * @returns {Promise<object|null>}
	 */
	read(pID)
	{
		return libFrameworkClient.getJSON(this.fable, this._url(this.entityName + '/' + pID))
			.then((pBody) => (pBody && pBody[this.idField]) ? pBody : ((pBody && Array.isArray(pBody.Records) && pBody.Records[0]) || (Array.isArray(pBody) ? pBody[0] : pBody) || null))
			.catch(() => null);
	}

	// ── polymorphic owner (OwnerType + IDOwner) ───────────────────────────────
	// A whiteboard attaches to any entity by OwnerType + IDOwner, or stands alone when OwnerType is
	// empty. These generalize the vision/user helpers below (which now delegate here). An owner can
	// have many boards; the detail page lists them and lets you add more.

	/**
	 * Whiteboards attached to one owner (e.g. ('WorkItem', 42)). Empty owner type lists standalone.
	 * @returns {Promise<Array<object>>}
	 */
	listForOwner(pOwnerType, pIDOwner)
	{
		let tmpFilter = 'FBV~OwnerType~EQ~' + (pOwnerType || '') + '~FBV~IDOwner~EQ~' + (pIDOwner || 0);
		return libFrameworkClient.getJSON(this.fable, this._url(this.pluralRoute + '/FilteredTo/' + tmpFilter + '/0/200'))
			.then((pBody) => Array.isArray(pBody) ? pBody : (pBody && pBody.Records) || [])
			.catch(() => []);
	}

	/**
	 * Create a new (empty) whiteboard for an owner. Dual-writes the legacy IDVision/IDUser column so
	 * transitional reads still resolve. Pass an empty OwnerType for a standalone board.
	 * @returns {Promise<object>} the created Moodboard row
	 */
	createForOwner(pOwnerType, pIDOwner, pName)
	{
		let tmpEmpty = { Nodes: [], Connections: [], ViewState: { PanX: 0, PanY: 0, Zoom: 1 } };
		let tmpRecord = { Name: pName || 'Whiteboard', OwnerType: pOwnerType || '', IDOwner: pIDOwner || 0, BoardJSON: JSON.stringify(tmpEmpty) };
		if (pOwnerType === 'Vision') { tmpRecord.IDVision = pIDOwner; }
		else if (pOwnerType === 'User') { tmpRecord.IDUser = pIDOwner; }
		return this.create(tmpRecord).then((pCreated) => (pCreated && pCreated[this.idField]) ? pCreated : ((pCreated && pCreated.Record) || pCreated));
	}

	/**
	 * Attach an existing whiteboard to an owner (re-pointing it from standalone or from another owner).
	 * The inverse of _disconnectBoard: it writes OwnerType/IDOwner and keeps the legacy IDVision column in
	 * step (set for a Vision target, cleared otherwise) so listForVision stays authoritative. The board's
	 * content is untouched -- only where it lives changes. Pass an empty OwnerType to make it standalone.
	 * @returns {Promise<object>} the updated Moodboard row
	 */
	attachToOwner(pID, pOwnerType, pIDOwner)
	{
		let tmpRecord = { OwnerType: pOwnerType || '', IDOwner: pIDOwner || 0, IDVision: (pOwnerType === 'Vision') ? (pIDOwner || 0) : 0 };
		return this.update(pID, tmpRecord);
	}

	/**
	 * The standalone Whiteboards library for the tenant. Excludes personal PROFILE boards (the IDUser
	 * column is stamped on those): a profile board belongs to a user's profile page, and listing every
	 * tenant board here put one user's profile board -- and its BoardJSON -- in front of the whole tenant.
	 * Vision / WorkItem boards stay (team content). Tenancy + this filter are enforced server-side.
	 * @returns {Promise<Array<object>>}
	 */
	listAllForCustomer()
	{
		let tmpFilter = 'FBV~IDUser~EQ~0';
		return libFrameworkClient.getJSON(this.fable, this._url(this.pluralRoute + '/FilteredTo/' + tmpFilter + '/0/500'))
			.then((pBody) => Array.isArray(pBody) ? pBody : (pBody && pBody.Records) || [])
			.catch(() => []);
	}

	/**
	 * The whiteboards attached to an entity, for the detail-page board section. Visions read by their
	 * legacy IDVision column (catches boards made before OwnerType existed, with no prod backfill
	 * needed); every other entity type reads by OwnerType/IDOwner.
	 * @returns {Promise<Array<object>>}
	 */
	listForEntity(pEntityType, pID)
	{
		if (pEntityType === 'Vision') { return this.listForVision(pID); }
		return this.listForOwner(pEntityType, pID);
	}

	/**
	 * The Moodboard rows owned by a user (their personal profile board). A user has at most one.
	 * Reads by the legacy IDUser column so existing profile boards resolve without a backfill.
	 * @param {number} pIDUser
	 * @returns {Promise<Array<object>>}
	 */
	listForUser(pIDUser)
	{
		let tmpFilter = 'FBV~IDUser~EQ~' + pIDUser;
		return libFrameworkClient.getJSON(this.fable, this._url(this.pluralRoute + '/FilteredTo/' + tmpFilter + '/0/100'))
			.then((pBody) => Array.isArray(pBody) ? pBody : (pBody && pBody.Records) || [])
			.catch(() => []);
	}

	/**
	 * The user's personal profile board, creating an empty one the first time. Call this only for the
	 * signed-in user (creating a board stamps it to them); for a teammate, use listForUser and show
	 * read-only (do not create on their behalf).
	 * @param {number} pIDUser
	 * @returns {Promise<object>} the Moodboard row
	 */
	getOrCreatePersonal(pIDUser)
	{
		return this.listForUser(pIDUser).then((pRows) =>
		{
			if (pRows && pRows.length) { return pRows[0]; }
			let tmpEmpty = { Nodes: [], Connections: [], ViewState: { PanX: 0, PanY: 0, Zoom: 1 } };
			return this.create({ Name: 'Profile board', IDUser: pIDUser, BoardJSON: JSON.stringify(tmpEmpty) })
				.then((pCreated) => (pCreated && pCreated[this.idField]) ? pCreated : ((pCreated && pCreated.Record) || pCreated));
		});
	}

	/**
	 * The Moodboard rows tied to a Vision (its inspiration board). A Vision has at most one.
	 * @param {number} pIDVision
	 * @returns {Promise<Array<object>>}
	 */
	listForVision(pIDVision)
	{
		let tmpFilter = 'FBV~IDVision~EQ~' + pIDVision;
		return libFrameworkClient.getJSON(this.fable, this._url(this.pluralRoute + '/FilteredTo/' + tmpFilter + '/0/100'))
			.then((pBody) => Array.isArray(pBody) ? pBody : (pBody && pBody.Records) || [])
			.catch(() => []);
	}

	/**
	 * A Vision's inspiration board, creating an empty one the first time. Unlike the personal board this
	 * one is shared (whoever can edit the Vision's content edits it), so create it whenever missing. The
	 * server stamps IDCustomer; the client supplies IDVision the same way the personal board supplies
	 * IDUser.
	 * @param {number} pIDVision
	 * @param {string} pName - a label for a freshly created board (e.g. the Vision's name)
	 * @returns {Promise<object>} the Moodboard row
	 */
	getOrCreateForVision(pIDVision, pName)
	{
		return this.listForVision(pIDVision).then((pRows) =>
		{
			if (pRows && pRows.length) { return pRows[0]; }
			let tmpEmpty = { Nodes: [], Connections: [], ViewState: { PanX: 0, PanY: 0, Zoom: 1 } };
			return this.create({ Name: pName || 'Inspiration board', IDVision: pIDVision, BoardJSON: JSON.stringify(tmpEmpty) })
				.then((pCreated) => (pCreated && pCreated[this.idField]) ? pCreated : ((pCreated && pCreated.Record) || pCreated));
		});
	}

	/**
	 * Persist a board object onto a Moodboard row. Sends BoardJSON as a string so it round-trips the
	 * same regardless of whether meadow treats the column as text or JSON.
	 * @param {number} pID - IDMoodboard
	 * @param {object} pBoard - the board { Nodes, Connections, ViewState }
	 * @param {object} pExtra - optional extra fields to update alongside (e.g. { Name })
	 */
	saveBoard(pID, pBoard, pExtra)
	{
		let tmpRecord = Object.assign({}, pExtra || {});
		tmpRecord.BoardJSON = JSON.stringify(pBoard || _EMPTY_BOARD);
		return this.update(pID, tmpRecord);
	}
}

module.exports = PictProviderMoodboardAPI;
module.exports.default_configuration = _DefaultProviderConfiguration;
